import {getLocalizationBasedOnLang} from '../../data/models/User/UserInfo';

const english = {
  title: 'Posts',
  newPost: 'New post',
  createPost: 'Create post',
  cancel: 'Cancel',
  emptyTitle: 'No posts yet',
  emptySubtitle: 'Share images and videos with your learners in a single post',
  searchEmptyTitle: 'Nothing found',
  searchEmptySubtitle: 'No post matches your search',
  createTitle: 'New post',
  inProject: (name: string) => `Project: ${name}`,
  sourceTitle: 'Media',
  sourceEmptyTitle: 'Add images and videos',
  sourceHint: 'Choose from your media library or upload new files.',
  sourceLimitHint: (max: number) => `Up to ${max} images or videos per post.`,
  selectMedia: 'Add media',
  addMore: 'Add more',
  itemsCount: (count: number, max: number) => `${count} / ${max} items`,
  postTitle: 'Title',
  postTitlePlaceholder: 'Title',
  caption: 'Caption',
  captionPlaceholder: 'Write a caption for your post',
  preview: 'Preview',
  previewTitle: 'Post preview',
  exitPreview: 'Exit preview',
  unmute: 'Tap for sound',
  pauseProgress: 'Pause automatic progression',
  resumeProgress: 'Resume automatic progression',
  fillFrame: 'Fill the screen (crops the sides)',
  fitToFrame: 'Fit the whole item',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  moveUp: 'Move up',
  moveDown: 'Move down',
  imageLabel: 'Image',
  videoLabel: 'Video',
  remove: 'Remove',
  save: 'Save',
  renameMedia: 'Rename',
  renameMediaTitle: 'Rename item',
  mediaName: 'Name',
  mediaNamePlaceholder: 'Name this image or video',
  removeMedia: 'Remove from post',
  removeMediaConfirm:
    'Are you sure you want to remove this item from the post?',
  lastMediaHint: 'A post has to keep at least one item',
  open: 'Open',
  editInfo: 'Edit info',
  edit: 'Edit',
  postPosterHeading: 'Post poster',
  posterReplace: 'Replace',
  posterRemove: 'Remove',
  posterEmptyTitle: 'Click to select an image',
  posterEmptyHint: 'JPG or PNG, 9:16 works best',
  posterHint: 'Shown in the feed before the post opens.',
  posterEmptyStateHint: 'Optional, the first item of the post is used.',
  editInfoTitle: 'Edit post info',
  learners: 'Learners',
  moveToProject: 'Move to project',
  delete: 'Delete',
  deleteConfirm: 'Are you sure you want to delete this post?',
  itemsLabel: (count: number) => (count === 1 ? '1 item' : `${count} items`),
  updatedAgo: (timeAgo: string) => `Updated ${timeAgo}`,
  notShared: 'Not shared',
  viewLearnerProgress: 'View learner progress',
  allPosts: 'All posts',
  mediaHeading: 'Media',
  mediaUnavailable: 'The media of this post could not be loaded',
  nowPlaying: 'Now playing',
  analytics: 'Analytics',
  analyticsNeedPublish: 'Publish the post to see analytics',
  dragOrderHint: 'Use the arrows to change the order learners see',
  shareTitle: 'Get this in front of learners',
  shareHint: 'Share the post so it appears in their feed.',
  shareWithLearners: 'Share with learners',
  videoTooLong: 'Videos in a post can be up to 5 minutes long',
  videoNotReady: 'This video is still processing. Try again once it is ready',
  maxItemsReached: 'A post can hold up to 10 items',
  unsupportedType: 'Only images and videos can be added to a post',
  postCreated: 'Post created',
  postUpdated: 'Post updated',
  postDeleted: 'Post deleted',
  orderUpdated: 'Order updated',
  mediaRenamed: 'Item renamed',
  mediaRemoved: 'Item removed',
};

const welsh = {
  title: 'Posts',
  newPost: 'Post newydd',
  createPost: 'Creu post',
  cancel: 'Canslo',
  emptyTitle: 'Dim postiadau eto',
  emptySubtitle: 'Rhannwch ddelweddau a fideos gyda’ch dysgwyr mewn un post',
  searchEmptyTitle: 'Heb ganfod dim',
  searchEmptySubtitle: 'Nid oes post yn cyfateb i’ch chwiliad',
  createTitle: 'Post newydd',
  inProject: (name: string) => `Prosiect: ${name}`,
  sourceTitle: 'Cyfryngau',
  sourceEmptyTitle: 'Ychwanegu delweddau a fideos',
  sourceHint:
    'Dewiswch o’ch llyfrgell gyfryngau neu llwythwch ffeiliau newydd.',
  sourceLimitHint: (max: number) =>
    `Hyd at ${max} delwedd neu fideo fesul post.`,
  selectMedia: 'Ychwanegu cyfryngau',
  addMore: 'Ychwanegu rhagor',
  itemsCount: (count: number, max: number) => `${count} / ${max} eitem`,
  postTitle: 'Teitl',
  postTitlePlaceholder: 'Teitl',
  caption: 'Capsiwn',
  captionPlaceholder: 'Ysgrifennwch gapsiwn ar gyfer eich post',
  preview: 'Rhagolwg',
  previewTitle: 'Rhagolwg o’r post',
  exitPreview: 'Gadael y rhagolwg',
  unmute: 'Tapiwch am sain',
  pauseProgress: 'Oedi’r symud awtomatig',
  resumeProgress: 'Ailddechrau’r symud awtomatig',
  fillFrame: 'Llenwi’r sgrin (tocio’r ochrau)',
  fitToFrame: 'Ffitio’r eitem gyfan',
  moveLeft: 'Symud i’r chwith',
  moveRight: 'Symud i’r dde',
  moveUp: 'Symud i fyny',
  moveDown: 'Symud i lawr',
  imageLabel: 'Delwedd',
  videoLabel: 'Fideo',
  remove: 'Tynnu',
  save: 'Cadw',
  renameMedia: 'Ailenwi',
  renameMediaTitle: 'Ailenwi’r eitem',
  mediaName: 'Enw',
  mediaNamePlaceholder: 'Rhowch enw i’r ddelwedd neu’r fideo hwn',
  removeMedia: 'Tynnu o’r post',
  removeMediaConfirm:
    'Ydych chi’n siŵr eich bod am dynnu’r eitem hon o’r post?',
  lastMediaHint: 'Rhaid i bost gadw o leiaf un eitem',
  open: 'Agor',
  editInfo: 'Golygu gwybodaeth',
  edit: 'Golygu',
  postPosterHeading: 'Poster y post',
  posterReplace: 'Amnewid',
  posterRemove: 'Tynnu',
  posterEmptyTitle: 'Cliciwch i ddewis delwedd',
  posterEmptyHint: "JPG neu PNG, 9:16 sy'n gweithio orau",
  posterHint: "Yn ymddangos yn y ffrwd cyn i'r post agor.",
  posterEmptyStateHint: 'Dewisol, defnyddir eitem gyntaf y post.',
  editInfoTitle: 'Golygu gwybodaeth y post',
  learners: 'Dysgwyr',
  moveToProject: 'Symudwch i brosiect',
  delete: 'Dileu',
  deleteConfirm: 'Ydych chi’n siŵr eich bod am ddileu’r post hwn?',
  itemsLabel: (count: number) => (count === 1 ? '1 eitem' : `${count} eitem`),
  updatedAgo: (timeAgo: string) => `Diweddarwyd ${timeAgo}`,
  notShared: 'Heb ei rannu',
  viewLearnerProgress: 'Gweld cynnydd dysgwyr',
  allPosts: 'Pob post',
  mediaHeading: 'Cyfryngau',
  mediaUnavailable: 'Nid oedd modd llwytho cyfryngau’r post hwn',
  nowPlaying: 'Yn chwarae nawr',
  analytics: 'Analytics',
  analyticsNeedPublish: 'Cyhoeddwch y post i weld dadansoddeg',
  dragOrderHint:
    'Defnyddiwch y saethau i newid y drefn y mae dysgwyr yn ei gweld',
  shareTitle: 'Rhowch hwn o flaen dysgwyr',
  shareHint: 'Rhannwch y post fel ei fod yn ymddangos yn eu ffrwd.',
  shareWithLearners: 'Rhannu gyda dysgwyr',
  videoTooLong: 'Gall fideos mewn post fod hyd at 5 munud',
  videoNotReady:
    'Mae’r fideo hwn yn dal i gael ei brosesu. Ceisiwch eto pan fydd yn barod',
  maxItemsReached: 'Gall post ddal hyd at 10 eitem',
  unsupportedType: 'Dim ond delweddau a fideos y gellir eu hychwanegu at bost',
  postCreated: 'Post wedi’i greu',
  postUpdated: 'Post wedi’i ddiweddaru',
  postDeleted: 'Post wedi’i ddileu',
  orderUpdated: 'Trefn wedi’i diweddaru',
  mediaRenamed: 'Eitem wedi’i hailenwi',
  mediaRemoved: 'Eitem wedi’i thynnu',
};

export const postsText: any = getLocalizationBasedOnLang(english, welsh);
